import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { toast } from "react-toastify";

import Dashboard from "../../../common/components/DetailsDashboard/DetailsDashboard";
import Sidebar from "../../../common/components/Main Menu/MainMenu";
import Breadcrumb from "../../../common/components/NavigationBreadcrumb/Breadcrumb";
import Spinner from "../../../common/components/Spinner/Spinner";
import InsightPDFDownload from "../../../common/components/InsightPanel/InsightPDFDownload";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import FeedbackPopup from "../../../common/components/Popups/FeedbackPopup";
import InsightNavigator from "../../../common/components/InsightPanel/InsightNavigator";
import CallReviewSheetPopup from "../../../common/components/Popups/CallReviewSheetPopup";

import { decodeId } from "../../../common/Helper/encdec";
import { postFeedback } from "../../services/postFeedback";
import {
  postCallReview,
  previewCallReviewEmail,
} from "../../services/postCallReview";
import { getCallReviewFeedback } from "../../services/getCallReview";
import {
  resetDetailsData,
  updateFeedbackStatus,
  getOperationDataInit,
} from "../../../redux/slices/operationHomeSlice";

import "./DetailsScreen.css";

const DetailsScreen = () => {
  const { memberId, callId } = useParams();
  const dispatch = useDispatch();

  const decodedMemberId = decodeId(memberId);
  const decodedCallId = decodeId(callId);
  const decodeMemberIdSpace = decodedMemberId?.replace(/^(CC)(\d+)/i, "$1 $2");
  console.log(decodeMemberIdSpace, decodedCallId);

  // ---------------- STATE ----------------
  const [triggerDownload, setTriggerDownload] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(2);
  const [observationText, setObservationText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Display info for the currently-viewed call, reported by the Dashboard. Lets
  // the top navigator show the correct call in its header even when that call
  // isn't part of the navigator's own list.
  const [reportedCallInfo, setReportedCallInfo] = useState(null);

  // The route's callId is the single source of truth for which call is on
  // screen (every navigation path — the Meeting Date arrows, the navigator
  // arrows and its dropdown — goes through the URL). Only trust the Dashboard's
  // report while it still describes that call; otherwise fall back to a stub so
  // the navigator can always locate the current call in its list and compute
  // real prev/next neighbours.
  const currentCallInfo = useMemo(() => {
    if (reportedCallInfo?.callId === decodedCallId) return reportedCallInfo;
    if (!decodedCallId) return null;
    return { callId: decodedCallId, memberId: decodeMemberIdSpace };
  }, [reportedCallInfo, decodedCallId, decodeMemberIdSpace]);

  // Review context lifted from InsightPanel. Non-community (no-scoring) calls
  // get a feedback-only Reviewer Feedback modal beside "Download PDF".
  const [reviewContext, setReviewContext] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // Incrementing signal to open the in-panel scoring modal for community calls.
  const [openScoringSignal, setOpenScoringSignal] = useState(0);
  // Saved reviewer feedback for non-scoring calls, so the modal reopens in edit
  // mode (like Edit Scores).
  const [savedFeedback, setSavedFeedback] = useState(null);

  // The review button is hidden for agents (only non-agents review calls).
  const userRole = useSelector(
    (state) => state.login?.login?.data?.data?.[0]?.Role,
  );
  const isAgent = (userRole || "").trim().toLowerCase() === "agent";

  // ---------------- REDUX ----------------
  const loading = useSelector((state) => state.operationHome.loading);
  const tableData = useSelector((state) => state.operationHome.table.data);
  const pageSize = useSelector((state) => state.operationHome.table.pageSize);

  // Re-fetch table data if Redux was cleared by a page refresh
  useEffect(() => {
    if (tableData.length === 0) {
      dispatch(getOperationDataInit({ name: "last365Days", limit: pageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const feedbackOptions = [
    "All good",
    "Minor observation",
    "Critical issues observed",
    "Enhancement Suggestion",
  ];

  useEffect(() => {
    return () => {
      dispatch(resetDetailsData());
    };
  }, [dispatch]);

  // Logic to check if feedback was already given
  const existingRecord = useMemo(() => {
    return tableData.find(
      (item) =>
        item.memberId === decodeMemberIdSpace && item.callId === decodedCallId,
    );
  }, [tableData, decodeMemberIdSpace, decodedCallId]);

  console.log(existingRecord);

  const isFeedbackGiven = existingRecord?.FeedbackStatus === true;

  // ---------------- HANDLERS ----------------

  const handleDownloadClick = () => {
    // 1. Trigger the download component
    setTriggerDownload(true);

    // 2. Show the toast message
    toast.info("Download started", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      icon: "📄",
    });
  };

  const handleFeedbackClick = () => {
    setIsFeedbackOpen(true);
  };

  // Logged-in user (from AdminDetails, cached in loginState) — used as the
  // reviewer name and the CC recipient on the feedback email.
  const getLoggedInUser = () => {
    try {
      const ls = localStorage.getItem("loginState");
      const u = ls ? JSON.parse(ls)?.data?.[0] : null;
      if (!u) return {};
      return {
        name: `${u.FirstName || ""} ${u.LastName || ""}`
          .replace(/\bnull\b/gi, "")
          .trim(),
        email: u.Email || "",
      };
    } catch {
      return {};
    }
  };

  // Load any previously-saved feedback for the current non-scoring call so the
  // modal reopens in edit mode.
  useEffect(() => {
    if (!reviewContext?.callId || reviewContext.isCommunityOrigin) {
      setSavedFeedback(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const fb = await getCallReviewFeedback(reviewContext.callId);
      if (!cancelled) setSavedFeedback(fb);
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewContext?.callId, reviewContext?.isCommunityOrigin]);

  // Feedback-only save handler for non-scoring calls. Called for every share
  // action (Email / Download / Not Now) so the feedback is always persisted;
  // the email is only sent when `options.sendEmail` is set.
  const handleFeedbackSubmit = async (payload, options = {}) => {
    const sendEmail = !!options.sendEmail;
    const feedback = {
      whatWentWell: payload?.whatWentWell || "",
      whatCanImprove: payload?.whatCanImprove || "",
      generalComments: payload?.generalComments || "",
    };
    try {
      const user = getLoggedInUser();

      // Preview: same payload, rendered rather than sent or saved. Returned
      // straight to the caller so the modal can display it.
      if (options.previewOnly) {
        return await previewCallReviewEmail({
          feedbackOnly: true,
          callid: reviewContext?.callId || decodedCallId,
          reviewerName: user.name,
          senderEmail: user.email,
          convoUrl: typeof window !== "undefined" ? window.location.href : "",
          ...feedback,
        });
      }
      const res = await postCallReview({
        feedbackOnly: true,
        sendEmail,
        callid: reviewContext?.callId || decodedCallId,
        reviewerName: user.name,
        senderEmail: user.email,
        // Only a real edit updates "Reviewed By" / "Last Updated". This
        // handler also runs for Email and Download, which must persist the
        // feedback but shouldn't claim the sender reviewed it.
        contentChanged: options.contentChanged !== false,
        convoUrl: typeof window !== "undefined" ? window.location.href : "",
        // The body as the reviewer edited it in the preview. Without this the
        // edit was dropped here and the backend rebuilt the message from the
        // stored feedback, so the agent received the text as it stood before
        // the edit. Omitted entirely when untouched, so an unedited send still
        // gets a freshly rendered message.
        ...(options.emailHtmlOverride != null
          ? { emailHtmlOverride: options.emailHtmlOverride }
          : {}),
        ...feedback,
      });
      // Cache locally so reopening the modal shows the latest feedback.
      setSavedFeedback({
        ...feedback,
        updatedAt: res?.updatedAt || res?.UpdatedAt || res?.reviewedAt,
      });
      if (sendEmail) {
        if (res?.emailSent) {
          toast.success("Feedback emailed to the agent successfully");
        } else {
          toast.error(
            res?.emailError || "Failed to email feedback to the agent",
          );
        }
        setShowFeedbackModal(false);
      } else {
        toast.success("Reviewer feedback saved successfully");
      }
      return res;
    } catch (err) {
      if (sendEmail) toast.error("Failed to email feedback to the agent");
      return undefined;
    }
  };

  const handleSubmitFeedback = async () => {
    if (!observationText.trim()) {
      toast.warning("Please enter observation");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        callId: decodedCallId,
        customerId: decodeMemberIdSpace,
        feedback: feedbackOptions[selectedObservation - 1],
        observation: observationText,
      };

      await postFeedback(payload);

      dispatch(
        updateFeedbackStatus({
          memberId: decodeMemberIdSpace,
          callId: decodedCallId,
        }),
      );

      setIsFeedbackOpen(false);
      toast.success(
        isFeedbackGiven ? "Feedback updated!" : "Feedback submitted!",
        { icon: false },
      );
    } catch (error) {
      toast.error("❌ Failed to process feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "Home", link: "/landing" },
    { label: "Ops Portal", link: "/operationHome", state: { from: "details" } },
    { label: "Insight Details" },
  ];

  return (
    <>
      {loading && <Spinner loading={loading} />}

      <div className="main details-page-main">
        <Navbar />
        <div className="App details-app">
          <div className="details-main">
            <div className="details-sidebar">
              {/* <Sidebar logoLink="/landing" /> */}
            </div>

            <div className="details-secondary">
              <div className="breadcrumb-download-container">
                <Breadcrumb
                  breadcrumbItems={breadcrumbItems}
                  backLink="/operationHome"
                  backLinkState={{ from: "details" }}
                />

               

                <div className="actions-group">
                  <div className="feedback-btn-wrapper">
                    {isFeedbackGiven && (
                      <div className="feedback-success-badge">✓</div>
                    )}
                    <span
                      className="action-btn feedback-hover"
                      onClick={handleFeedbackClick}
                    >
                      Product Feedback
                    </span>
                  </div>

                  {/* Top-right review button for every call (non-agents only).
                      Community-origin calls open the existing scoring (Edit
                      Scores) modal; other categories open the feedback-only
                      modal (no scores). */}
                  {!isAgent &&
                    reviewContext?.ready &&
                    (reviewContext.isCommunityOrigin ? (
                      reviewContext.scoringReady && (
                        <span
                          className="action-btn feedback-hover"
                          onClick={() =>
                            setOpenScoringSignal((n) => n + 1)
                          }
                        >
                          Edit Scores
                        </span>
                      )
                    ) : (
                      <span
                        className="action-btn feedback-hover"
                        onClick={() => setShowFeedbackModal(true)}
                      >
                        Reviewer Feedback
                      </span>
                    ))}

                  {/* UPDATED: OnClick handler changed here */}
                  <span
                    className={`action-btn download-pdf-hover${isPdfGenerating ? " disabled" : ""}`}
                    onClick={!isPdfGenerating ? handleDownloadClick : undefined}
                    style={
                      isPdfGenerating
                        ? {
                            opacity: 0.6,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                          }
                        : undefined
                    }
                  >
                    {isPdfGenerating ? "Generating..." : "Download PDF"}
                  </span>
                </div>
              </div>

               <div className="details-nav-center">
                  <InsightNavigator
                    currentCallId={decodedCallId}
                    currentCallInfo={currentCallInfo}
                    disabled={isPdfGenerating}
                  />
                </div>

              <Dashboard
                memberId={decodedMemberId}
                callId={decodedCallId}
                onCurrentCallInfo={setReportedCallInfo}
                onReviewContext={setReviewContext}
                openScoringSignal={openScoringSignal}
                navigationDisabled={isPdfGenerating}
              />

              {/* ---------------- FEEDBACK POPUP ---------------- */}
              <FeedbackPopup
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
                selectedObservation={selectedObservation}
                setSelectedObservation={setSelectedObservation}
                observationText={observationText}
                setObservationText={setObservationText}
                onSubmit={handleSubmitFeedback}
                isSubmitting={isSubmitting}
                isFeedbackGiven={isFeedbackGiven}
              />

              {/* ------ REVIEWER FEEDBACK (non-scoring call categories) ------ */}
              {reviewContext && !reviewContext.isCommunityOrigin && (
                <CallReviewSheetPopup
                  isOpen={showFeedbackModal}
                  onClose={() => setShowFeedbackModal(false)}
                  feedbackOnly
                  onSave={handleFeedbackSubmit}
                  initialData={
                    savedFeedback || {
                      whatWentWell: "",
                      whatCanImprove: "",
                      generalComments: "",
                    }
                  }
                  reviewData={savedFeedback}
                  callId={reviewContext.callId || decodedCallId}
                  agentName={reviewContext.agentName}
                  memberName={reviewContext.memberName}
                  reviewDate={reviewContext.reviewDate}
                  vonageCallId={reviewContext.vonageCallId}
                  magicHealthId={reviewContext.magicHealthId}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {triggerDownload && (
        <InsightPDFDownload
          triggerDownload={triggerDownload}
          setTriggerDownload={setTriggerDownload}
          memberId={decodedMemberId}
          callId={decodedCallId}
          onLoadingChange={setIsPdfGenerating}
        />
      )}
    </>
  );
};

export default DetailsScreen;
